import { PlayArrow } from '@mui/icons-material'
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Skeleton,
  Typography,
} from '@mui/material'
import useCurrentMenu from 'common/hooks/useCurrentMenu'
import useFetchTipoSalida from 'features/control_presencia/hooks/useFetchTipoSalida'
import FichajesController, {
  PresenceControlActions,
} from 'features/control_presencia/libs/FichajesController'
import { colorEntradaMap, tiposEntradaMap } from 'features/control_presencia/libs/tiposEntradaMap'
import { colorSalidaMap, tiposSalidaMap } from 'features/control_presencia/libs/tiposSalidaMap'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'

/**
 * @module ControlPresencia
 */

/**
 * Buttons to mark enter or leave of
 * the presence control
 *
 * @param {Object} props
 * @param {boolean} props.loading
 * @param {boolean} props.modal
 * @param {function} props.setLastIteration
 * @param {number} props.lastIteration
 * @param {import('common/contexts/CustomModalContext').CustomModalHandler} props.handleModal
 * @example
 * ...
 * <PresenceActions
 *  loading={loading}
 *  setLastIteration={setLastIteration}
 *  lastIteration={lastIteration}
 *  handleModal={handleModal}
 *  modal={modal}
 * />
 * ...
 * @return {JSX.Element}
 */
const PresenceActions = ({ loading, modal, setLastIteration, lastIteration, handleModal }) => {
  const menu = useCurrentMenu()
  const { response: tiposEntradaSalida } = useFetchTipoSalida()
  const [openBackdrop, setOpenBackdrop] = useState(false)

  /**
   * Mark a presence control registry
   * @async
   * @param {PresenceControlActions} type - The tipe of action to perform
   * @param {string} [exitCode] - If is a exit type this param must be provided
   * @returns {Promise<void>}
   * */
  async function markAction(type, exitCode) {
    try {
      setOpenBackdrop(true)
      await FichajesController.create(type, exitCode, null, menu?.actualMatch?.parametros?.TERMINAL)
      setLastIteration(lastIteration + 1)
      enqueueSnackbar('Guardado correctamente', {
        variant: 'success',
      })
    } catch (error) {
      enqueueSnackbar('Ha ocurrido un error', {
        variant: 'error',
      })
      console.error(error)
    } finally {
      setOpenBackdrop(false)
    }
  }

  return loading ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'flex-start',
        gap: 2,
        width: '100%',
      }}
    >
      <Skeleton variant="rounded" height={40} width={240} />
      <Skeleton variant="rounded" height={40} width={240} />
      <Skeleton variant="rounded" height={40} width={240} />
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'flex-start',
        gap: 2,
        width: '100%',
      }}
    >
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: '20px',
        }}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
        <Typography>Guardando Fichaje</Typography>
      </Backdrop>
      <Box>
        {!tiposEntradaSalida?.tipos_entrada || tiposEntradaSalida?.tipos_entrada?.length === 0 ? (
          <Button
            startIcon={<PlayArrow />}
            disabled={loading || modal}
            onClick={async () => {
              markAction(PresenceControlActions.Entrada)
            }}
            variant="text"
            color="success"
            sx={{
              flexGrow: 0,
              textWrap: 'nowrap',
              lineHeight: '3',
              fontSize: 'larger',
              px: 4,
            }}
            size="large"
          >
            Entrada
          </Button>
        ) : (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }} textAlign="left">
              Registrar Entrada
            </Typography>
            <Box
              sx={{
                flex: '1 1 0',
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {tiposEntradaSalida?.tipos_entrada?.map((entrance, index) => {
                const Icon = tiposEntradaMap[entrance.codigo] || tiposEntradaMap.DEFAULT
                const color = colorEntradaMap[entrance.codigo] || colorEntradaMap.DEFAULT
                return (
                  <Button
                    key={index}
                    startIcon={<Icon />}
                    disabled={loading || modal}
                    onClick={async () => {
                      markAction(PresenceControlActions.Entrada, entrance.codigo)
                    }}
                    variant="text"
                    color={color}
                    sx={{
                      textWrap: 'nowrap',
                      lineHeight: '3',
                      fontSize: 'larger',
                      px: 4,
                    }}
                  >
                    {entrance.descripcion}
                  </Button>
                )
              })}
            </Box>
          </Box>
        )}
      </Box>
      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'inherit' } }} />
      <Divider flexItem sx={{ display: { xs: 'inherit', sm: 'none' } }} />
      <Box>
        {tiposEntradaSalida?.tipos_salida?.length > 1 ? (
          <Typography variant="h6" sx={{ mb: 2 }} textAlign="left">
            Registrar Salida
          </Typography>
        ) : (
          <></>
        )}
        <Box
          sx={{ flex: '1 1 0', display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}
        >
          {tiposEntradaSalida?.tipos_salida?.map((exit, index) => {
            const Icon = tiposSalidaMap[exit.codigo] || tiposSalidaMap.DEFAULT
            const color = colorSalidaMap[exit.codigo] || colorSalidaMap.DEFAULT
            return (
              <Button
                key={index}
                startIcon={<Icon />}
                disabled={loading || modal}
                onClick={async () => {
                  markAction(PresenceControlActions.Salida, exit.codigo)
                }}
                variant="text"
                color={color}
                size={tiposEntradaSalida?.tipos_salida?.length === 1 ? 'large' : 'medium'}
                sx={{
                  textWrap: 'nowrap',
                  lineHeight: '3',
                  fontSize: 'larger',
                  px: 4,
                }}
              >
                {exit.descripcion}
              </Button>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export default PresenceActions
